/**
 * FFmpegProcessor wrappers: transparent WORKERFS streaming + memory guard.
 *
 * Every FFmpeg-using tool just calls `FFmpegProcessor.process(file, …)` (or the
 * multi-input / segments / concat / re-encode / probe variants). This file
 * decorates all of them so that:
 *
 *   1. When the input is a File or a Blob, the corresponding *Streaming*
 *      implementation is tried first — the input stays on disk (File.slice()
 *      via WORKERFS), FFmpeg reads bytes lazily, so a 1 GB 4K clip no longer
 *      has to fit in WASM heap twice just to extract one audio track.
 *
 *   2. If streaming can't be set up (mount failure, WORKERFS not supported on
 *      the current ffmpeg-core build, a test harness that monkey-patches the
 *      API, etc.) it falls back to the classic in-memory path — which keeps
 *      the FFmpegMemoryGuard pre-flight check against the device's RAM cap.
 *
 *   3. Real FFmpeg errors from a successful-mount run (OOM, codec crash,
 *      exit!=0) propagate out. Those aren't "streaming didn't work" — they're
 *      genuine failures the caller's retry ladder / typed-error routing
 *      should see.
 *
 * The memory guard policy for the classic path is unchanged: args are mapped
 * to an operation tag ('video-enhance', 'join', 'copy', etc.) and the guard
 * refuses early with a FileTooLargeError when the predicted peak memory would
 * blow the device cap. Tools can tag explicitly via `__nextOperation` when the
 * inferred operation would pick the wrong multiplier.
 */
(function(){
'use strict';

// Infer the operation tag from the argument list. Keep the mapping conservative
// — when in doubt, pick the HIGHER multiplier (refuse more aggressively) because
// a false positive is a friendly message while a false negative is a WASM crash.
function inferOperation(args){
    if (!Array.isArray(args) || args.length === 0) return 'default';
    var s = args.join(' ');

    // ⚠️ The video branch is tested BEFORE the copy branch, and the order is
    // the whole point. `-c:a copy` matches `-c(\:\w)?\s+copy` just as well as
    // `-c copy` does, so a FULL libx264 re-encode that merely passes the audio
    // through used to be priced as a remux — 'copy' (2.5×) instead of
    // 'video-filter' (7×). Measured 2026-08-19 on /video-to-vr's own first
    // attempt (`-vf …hstack… -c:v libx264 … -c:a copy` → 'copy'); the same
    // shape is the default in every tool that re-encodes video and leaves the
    // audio track alone. A command carrying a real video encoder is a video
    // re-encode no matter what it does with the audio.
    if (/libx264|libx265|libvpx|libaom/i.test(s) || /-vcodec\s+(?!copy)/i.test(s)) {
        if (/scale\s*=\s*(?:7680|3840)/i.test(s) && /\b(libx265|libvpx-vp9|libaom)/i.test(s)) return 'upscale-4x';
        if (/scale\s*=\s*(?:3840|2560|1920)/i.test(s)) return 'upscale-2x';
        if (/lut3d|nlmeans|unsharp|zscale|hqdn3d/i.test(s)) return 'video-enhance';
        if (/scale\s*=|crop\s*=|rotate\s*=|overlay/i.test(s)) return 'video-filter';
        return 'video-transcode';
    }

    // No video encoder in the command, so a `copy` here really is a stream
    // copy: `-c copy`, `-c:v copy`, `-c:a copy`, plus the legacy
    // `-acodec/-vcodec/-scodec copy` spellings. Before the legacy alternative
    // was added, extract-audio and friends fell through to 'default' (5×)
    // instead of 'copy' (2.5×) — telemetry 30 d before that fix: 138
    // extract-audio memory_guard rejections the right multiplier would have
    // admitted (50-100 MB files on a 4 GB-and-under device, cap 256 MB).
    if (/-c(\:\w)?\s+copy/i.test(s) || /-[avs]codec\s+copy/i.test(s)) return 'copy';

    // pcm_s16le / pcm_s24le / pcm_f32le / pcm_alaw / pcm_mulaw — uncompressed
    // PCM output (typical for `-c:a pcm_*` when exporting WAV). Without this
    // branch the inference fell through to `default` (5.0×) which mis-priced
    // simple gain operations on WAV outputs as if they were upscale ops —
    // root cause of TIMBRICA-FRONTEND-512 (Артём, 2026-05-24: 86 MB WAV-out
    // volume blocked by guard quoting "оценка 444 MB"). pcm_* in-mem cost is
    // ~3× input (read + decoded float32 + re-encoded int16 mux).
    if (/-c(\:a)?\s+pcm_(?:s16|s24|s32|f32|alaw|mulaw)/i.test(s) || /libmp3lame|libvorbis|libopus|aac|flac/i.test(s) || /-acodec\s+(?!copy)/i.test(s)) {
        if (/afftdn|anlmdn|arnndn/i.test(s)) return 'denoise';
        if (/asetrate|rubberband/i.test(s)) return 'pitch-shift';
        if (/atempo(?!,)/i.test(s)) return 'time-stretch';
        if (/silenceremove/i.test(s)) return 'silence-remove';
        if (/afade|acompressor|equalizer|compand|aecho|aphaser/i.test(s)) return 'audio-filter';
        return 'audio-transcode';
    }

    if (/amerge|amix|concat/i.test(s)) return 'join';
    if (/atrim|-ss/i.test(s)) return 'trim';

    return 'default';
}

// A mount-setup failure vs. a real FFmpeg error. We fall back only on the first.
function isMountSetupError(err){
    if (!err) return false;
    var msg = (err.message || '').toLowerCase();
    // WORKERFS is unsupported, the emscripten mount call threw, the core build
    // doesn't expose the streaming methods yet, or Blob lacks a stable ref.
    return msg.indexOf('mount') !== -1
        || msg.indexOf('workerfs') !== -1
        || msg.indexOf('is not a function') !== -1
        || msg.indexOf('createdir') !== -1
        || msg.indexOf('unknown fs') !== -1;
}

// Streaming is memory-efficient but fragile: WORKERFS reads via FileReaderSync
// from a worker, which fails with NotReadableError if the underlying File
// handle rots mid-read (tab backgrounded, OS storage eviction, cloud re-index).
// Below this threshold, the in-memory path is more reliable — file bytes are
// pre-read to Uint8Array on main thread, then the worker only touches bytes.
// Telemetry 2026-04-19: mute-video had 11/11 FILE_LOST at avg 72 MB, join
// had 3/9 at avg 2 MB, enhance-video had 28/65 at avg 5 MB. Real-memory
// guard (25% device-memory cap) covers the OOM risk for in-memory below this
// threshold. Raise only if perf telemetry shows a regression.
// Phase 2.3 — per-operation streaming thresholds. Video filters / encodes peak
// 5-10× the input on the WASM heap (decoder + filter ring + encoder in flight),
// so a 30 MB clip can blow a 4 GB phone on video-enhance even though a 30 MB
// audio file is fine in-memory. Default stays 100 MB for everything else
// (audio paths, simple copies). Numbers calibrated against the FFmpegMemoryGuard
// multiplier table — files at the threshold sit in the "could OOM if RAM is
// tight" zone where the streaming path is materially safer.
var STREAM_MIN_BYTES_DEFAULT = 100 * 1024 * 1024;
// Audio-family thresholds added 2026-05-21 after 30-day /join telemetry
// showed in-memory peak 7-9× input vs streaming peak 1.4-1.6× — but the
// 100 MB default meant streaming NEVER fired for typical audio tracks
// (most <30 MB). 209 join failures / 30 d, 69 sids hit FFMPEG_MEMORY_GUARD
// on /join alone — overwhelmingly preventable by routing through WORKERFS
// instead of WASM in-memory FS. Elena (feedback 2026-05-21): 36 MB of
// inputs (27 MB WAV + 2 MP3s) → tree_merge prep produced 181 MB combined
// FLAC intermediates → merge step processMulti predicted 452 MB peak vs
// 256 MB low-RAM cap → blocked. With streaming the same merge runs at
// ~1.5× combined input ≈ 270 MB on disk, ~50 MB WASM heap → fits.
// Thresholds set just above the streaming-overhead break-even (~5-10 MB)
// so tiny calls keep their fast in-mem path.
var STREAM_MIN_BYTES_PER_OP = {
    // Video — calibrated previously (Phase 2.3)
    // 'video-enhance' lowered from 30 MB → 8 MB on 2026-06-01 (Wave 2.B
    // audit): mobile memory_guard was rejecting 29-30 MB videos with
    // capMB=256 × multiplier=10 → predictedMB=295 (7 confirmed mobile
    // sessions / 16 h on Chrome+Safari mobile, file_size ~30.85 MB). The
    // comment block above already names 5-10 MB as the streaming-overhead
    // break-even point, so dropping the threshold there routes the entire
    // affected cohort onto the streaming path (which the cap doesn't gate)
    // without changing desktop behavior — in-mem stays for tiny <8 MB ops.
    'video-enhance':    8 * 1024 * 1024,
    'video-transcode': 50 * 1024 * 1024,
    'video-filter':    50 * 1024 * 1024,
    'upscale-2x':      30 * 1024 * 1024,
    'upscale-4x':      20 * 1024 * 1024,
    // Audio — calibrated against ffmpeg_mem telemetry 2026-05-21
    // `join` is multi-input via processMulti, which gates on
    // `every(canStream)`. A single small input would otherwise block
    // streaming for the WHOLE call (e.g. 40 MB + 0.5 MB pair → no stream)
    // — exactly Elena's case. The benefit of streaming for multi-input
    // join is proportional to COMBINED size, not per-input, so the
    // per-input threshold is effectively a "real File / non-empty" check.
    // 1 KB filters out garbage but admits every real audio track.
    'join':                  1024,         // multi-input merge — stream whenever inputs are real Files
    'audio-transcode': 10 * 1024 * 1024,   // empirical in-mem mult 7.32×, stream 1.37×
    'audio-filter':    10 * 1024 * 1024,   // empirical in-mem mult 6.76×, stream 2.5×
    'denoise':         10 * 1024 * 1024,   // worst-case in-mem stack (afftdn + adeclick)
    'pitch-shift':     10 * 1024 * 1024,
    'time-stretch':    10 * 1024 * 1024,
    'silence-remove':  10 * 1024 * 1024,
    'audio-to-notes':  20 * 1024 * 1024,
    'copy':            20 * 1024 * 1024,   // even -c copy benefits when concat-demuxing large intermediates
};
function canStream(input, operation){
    if (!(input instanceof File || input instanceof Blob)) return false;
    var min = (operation && STREAM_MIN_BYTES_PER_OP[operation]) || STREAM_MIN_BYTES_DEFAULT;
    if (input.size > 0 && input.size < min) return false;
    return true;
}

function buildGuardError(file, guard, operation){
    var ErrClass = (window.ToolErrors && window.ToolErrors.FileTooLargeError) || Error;
    var err = new ErrClass(file, 0);
    err.code = 'FFMPEG_MEMORY_GUARD';
    err.message = guard.userMessage;
    err.reason = (window.ToolErrors && window.ToolErrors.REASONS && window.ToolErrors.REASONS.MEMORY_GUARD) || 'memory_guard';
    err.predictedMB = guard.predictedMB;
    err.capMB = guard.capMB;
    err.operation = operation;
    return err;
}

function reportBlocked(file, operation, guard){
    try {
        if (window.__aeBlocked) window.__aeBlocked({
            error_type: 'memory_guard',
            reason: guard.reason,
            operation: operation,
            predictedMB: guard.predictedMB,
            capMB: guard.capMB,
            multiplier: guard.multiplier,
            file_size: file && file.size || 0
        });
    } catch(_){}
}

// Peak-memory telemetry — Chrome-only (performance.memory is a Chromium ext).
// Used to calibrate FFmpegMemoryGuard multipliers from real-world data.
function snapshotMemory(){
    if (performance && performance.memory) {
        try { return performance.memory.usedJSHeapSize; } catch(_){ return null; }
    }
    return null;
}

function recordMemoryUsage(memBefore, fileBytes, operation, result){
    if (memBefore === null) return;
    try {
        var memAfter = snapshotMemory();
        if (memAfter === null) return;
        var peakBytes = Math.max(0, memAfter - memBefore);
        var mult = fileBytes > 0 ? peakBytes / fileBytes : 0;
        if (mult > 0 && mult < 200 && window.__ae) {
            window.__ae('perf', {
                f: 'ffmpeg_mem',
                op: operation,
                inMB: Math.round(fileBytes / 1048576),
                peakMB: Math.round(peakBytes / 1048576),
                mult: Math.round(mult * 10) / 10,
                out: result && result.size ? Math.round(result.size / 1048576) : 0
            });
        }
    } catch(_){}
}

// Path discriminator — answers "streaming really took effect, or did we silently
// fall back to in-memory?" on devices without performance.memory (all mobile).
// Emitted once per FFmpeg call; tiny shape: path + op + input MB + mount duration.
// 'stream'                — streaming succeeded, input never hit WASM heap
// 'stream_fallback_inmem' — mount setup failed, caller ran in-memory path
// 'inmem_only'            — streaming skipped (small file / no streaming impl)
function emitPath(path, operation, fileSize, mountMs){
    if (typeof window.__ae !== 'function') return;
    try {
        window.__ae('feature_use', {
            f: 'ffmpeg_path',
            path: path,
            op: operation || 'default',
            sizeMB: Math.round((fileSize || 0) / 1048576),
            mount_ms: mountMs || 0
        });
    } catch(_){}
}

function consumeOperationTag(fp){
    var explicit = (fp && fp.__nextOperation) || null;
    if (fp && fp.__nextOperation) {
        try { delete fp.__nextOperation; } catch(_){ fp.__nextOperation = null; }
    }
    return explicit;
}

// Same one-shot contract as __nextOperation, for the OUTPUT-size guard.
// { durationS, width, height, fps?, crf? } — see the call site for why the
// wrapper cannot work these out from `args`.
function consumeEncodeTag(fp){
    var enc = (fp && fp.__nextEncode) || null;
    if (fp && fp.__nextEncode) {
        try { delete fp.__nextEncode; } catch(_){ fp.__nextEncode = null; }
    }
    return enc;
}

// One-shot too: the OUTPUT size a COPY lane expects (bytes). A `-c copy`
// piece has no frame to price, so checkVideoEncode() cannot see it — the
// tool declares `FFmpegProcessor.__nextOutputBytes = input × share` and the
// guard refuses above the MEMFS wall BEFORE the work (2026-09-17, the day the
// input kinds grew past that wall). Consumed by both doors, like __nextEncode.
function consumeOutputTag(fp){
    var n = (fp && +fp.__nextOutputBytes) || 0;
    if (fp && fp.__nextOutputBytes !== undefined) {
        try { delete fp.__nextOutputBytes; } catch(_){ fp.__nextOutputBytes = undefined; }
    }
    return n > 0 ? n : 0;
}

function refuseIfOutputTooLarge(fp, input, operation, _retry){
    var ob = consumeOutputTag(fp);
    if (_retry || !(ob > 0) || !window.FFmpegMemoryGuard || !window.FFmpegMemoryGuard.checkOutputBytes) return;
    var og = window.FFmpegMemoryGuard.checkOutputBytes({ outBytes: ob });
    if (!og.safe) {
        reportBlocked(input, operation, og);
        throw buildGuardError(input, og, operation);
    }
}

var _wrapAttempts = 0;
function wrapWhenReady(){
    var fp = window.FFmpegProcessor;
    // Wait for the REAL FFmpegProcessor — not the __sharedModule queue-stub.
    // timbrica-core installs `window.FFmpegProcessor = { __isStub: true, … }`
    // on every page so deferred callers don't hit `undefined`; ffmpeg-processor.js
    // then REPLACES window.FFmpegProcessor with the real object. If we decorate
    // the stub, that replacement silently drops the decoration — which is the
    // bug that left every FFmpeg tool on the in-memory path (no WORKERFS
    // streaming): a 1 GB video was copied whole into the WASM heap and OOM-
    // crashed the tab. Skipping __isStub makes the retry loop hold out for the
    // real object. Bounded so a failed ffmpeg-processor.js load doesn't spin a
    // 150 ms timer forever (~60 s ceiling).
    // CodecMuxRules is a hard dependency too: it owns the output contract this
    // file pins onto every command. It is defer-loaded AFTER this file in the
    // layout, so relying on script order alone would install the decorators
    // without the pin on a fast machine. Wait for it explicitly.
    if (!fp || fp.__isStub || !window.FFmpegMemoryGuard ||
        !(window.CodecMuxRules && CodecMuxRules.enforceVideoOutputContract)) {
        if (++_wrapAttempts < 400) setTimeout(wrapWhenReady, 150);
        return;
    }

    // ── .process ────────────────────────────────────────────────────────────
    if (fp.process && !fp.process.__autoGuarded) {
        var origProcess = fp.process;
        fp.process = async function(input, args, outName, inputName, preArgs, _retry){
            var explicitOp = consumeOperationTag(this);
            var operation = explicitOp || inferOperation(args);

            // ── Output-size guard, BOTH lanes ────────────────────────────
            // `FFmpegMemoryGuard.check()` below prices the INPUT, and it only
            // runs on the in-memory lane — a streamed input returns before it.
            // For a whole-file video re-encode neither is the thing that kills
            // the run: the OUTPUT grows in MEMFS (worker JS heap, measured
            // RangeError-death at 1897 MB written) and `+faststart` rewrites it
            // through a temp copy at the finish line. That is why
            // checkVideoEncode() was written on 2026-08-06 — and it then sat
            // wired into exactly one tool, so every other doomed run still
            // ground for an hour before dying. The wrapper cannot infer
            // duration or dimensions from `args`, so a tool that knows them
            // declares them:
            //
            //     FFmpegProcessor.__nextEncode = { durationS, width, height,
            //                                      fps, crf };
            //
            // consumed once, like __nextOperation. No declaration = no check
            // (unchanged behaviour), so this is additive for the other tools.
            var enc = consumeEncodeTag(this);
            if (!_retry && enc && window.FFmpegMemoryGuard.checkVideoEncode) {
                var vg = window.FFmpegMemoryGuard.checkVideoEncode({
                    durationS: enc.durationS, width: enc.width, height: enc.height,
                    fps: enc.fps, crf: enc.crf,
                    inputBytes: (input && input.size) || 0
                });
                if (!vg.safe) {
                    reportBlocked(input, operation, vg);
                    throw buildGuardError(input, vg, operation);
                }
            }
            refuseIfOutputTooLarge(this, input, operation, _retry);

            // Streaming attempt: input stays on disk, memory bounded by codec state
            if (!_retry && canStream(input, operation) && typeof fp.processStreaming === 'function') {
                var memBeforeStream = snapshotMemory();
                var streamT0 = Date.now();
                try {
                    var result = await fp.processStreaming(input, args, outName, inputName, preArgs);
                    emitPath('stream', operation, input.size, Date.now() - streamT0);
                    recordMemoryUsage(memBeforeStream, input.size || 0, operation + ':stream', result);
                    return result;
                } catch (err) {
                    if (!isMountSetupError(err)) throw err;
                    emitPath('stream_fallback_inmem', operation, input.size, Date.now() - streamT0);
                    console.warn('[ffmpeg] streaming mount failed, using in-memory path:', err.message);
                }
            } else if (!_retry && input && input.size) {
                emitPath('inmem_only', operation, input.size, 0);
            }

            // Classic in-memory path — pre-flight memory guard decides early
            if (!_retry && input && input.size && input.size > 0) {
                var guard = window.FFmpegMemoryGuard.check({ file: input, operation: operation });
                if (!guard.safe) {
                    reportBlocked(input, operation, guard);
                    throw buildGuardError(input, guard, operation);
                }
            }

            var memBefore = snapshotMemory();
            var res = await origProcess.call(this, input, args, outName, inputName, preArgs, _retry);
            recordMemoryUsage(memBefore, input && input.size || 0, operation, res);
            return res;
        };
        fp.process.__autoGuarded = true;
        fp.process.__original = origProcess;
    }

    // ── .processStreaming — the same OUTPUT-size guard for DIRECT callers ──
    // Tools that stream on their own (video-compressor's single pass,
    // reels-maker, video-loop, …) call processStreaming without going through
    // .process, so a declared __nextEncode stayed on the object unconsumed —
    // and was then eaten by the NEXT .process call (a probe, a remux), pricing
    // the wrong operation with the wrong numbers. The declaration is one-shot
    // either way: .process consumes it before its own streaming attempt lands
    // here, so a call routed through .process is never priced twice. No
    // declaration = no check, exactly as on .process.
    if (fp.processStreaming && !fp.processStreaming.__autoGuarded) {
        var origStreaming = fp.processStreaming;
        fp.processStreaming = async function(input, args, outName, inputName, preArgs){
            var enc = consumeEncodeTag(this);
            if (enc && window.FFmpegMemoryGuard && window.FFmpegMemoryGuard.checkVideoEncode) {
                var operation = consumeOperationTag(this) || inferOperation(args);
                var vg = window.FFmpegMemoryGuard.checkVideoEncode({
                    durationS: enc.durationS, width: enc.width, height: enc.height,
                    fps: enc.fps, crf: enc.crf,
                    inputBytes: (input && input.size) || 0
                });
                if (!vg.safe) {
                    reportBlocked(input, operation, vg);
                    throw buildGuardError(input, vg, operation);
                }
            }
            refuseIfOutputTooLarge(this, input, inferOperation(args), false);
            return origStreaming.call(this, input, args, outName, inputName, preArgs);
        };
        fp.processStreaming.__autoGuarded = true;
        fp.processStreaming.__original = origStreaming;
    }

    // ── recovery wrapper on top of the auto-guarded .process ─────────────────
    // Catches container-error failures (moov atom not found / Invalid data /
    // Aborted on demux) and re-runs the guarded process with a clean WAV
    // produced by the browser's native decoder. Skips video-* operations,
    // metadata-only ops (would lose tags), and copy operations (no transcode
    // happening — recovery would change semantics). Ops checked AFTER the
    // first failure so we don't pay the cost on healthy files.
    if (fp.process && fp.process.__autoGuarded && !fp.process.__recoveryWrapped) {
        var initialGuarded = fp.process;
        var recoveryWrapper = async function(input, args, outName, inputName, preArgs, _retry){
            // Read the inner function fresh from the property each call. Two
            // reasons: (a) tests can swap fp.process.__guardedOriginal to a
            // mock without rebuilding the wrap chain, (b) future hot-reloads
            // of FFmpegProcessor pick up the new function automatically.
            var guarded = recoveryWrapper.__guardedOriginal || initialGuarded;

            // Recovery only on the first attempt. Manual retries (ffmpeg
            // reinit + retry inside origProcess) don't re-trigger recovery
            // to avoid loops.
            if (_retry || !window.AudioRecover) {
                return await guarded.call(this, input, args, outName, inputName, preArgs, _retry);
            }
            // Already-recovered files take the normal path — they're clean WAVs.
            // Use both an inline flag and a fallback WeakSet because File
            // instances are sometimes frozen (some Safari versions).
            var alreadyRecovered = !!(input && input.__audioRecovered) ||
                (window.__audioRecoveredSet && input && window.__audioRecoveredSet.has && window.__audioRecoveredSet.has(input));
            if (alreadyRecovered) {
                return await guarded.call(this, input, args, outName, inputName, preArgs, _retry);
            }
            // Skip recovery for non-audio inputs (video/image/etc). Replacing
            // a video with WAV would discard the user's intent.
            if (!window.AudioRecover.isAudioInput(input)) {
                return await guarded.call(this, input, args, outName, inputName, preArgs, _retry);
            }
            // Skip metadata/copy/video ops up-front — checking after failure
            // would still work but wastes a decode attempt on irrelevant ops.
            var op = inferOperation(args);
            if (op === 'metadata' || op === 'copy' || /^video-/.test(op)) {
                return await guarded.call(this, input, args, outName, inputName, preArgs, _retry);
            }

            try {
                return await guarded.call(this, input, args, outName, inputName, preArgs, _retry);
            } catch (origErr) {
                if (!window.AudioRecover.isContainerError(origErr)) throw origErr;

                var repaired;
                try {
                    // Forward whichever cancellation signal is active for the
                    // current FFmpeg run (set by FFmpegProcessor.processWithSignal).
                    // Without this the recovery path keeps running after Cancel
                    // — decode + WAV re-encode of a corrupt file can take 5-15 s.
                    repaired = await window.AudioRecover.tryRepair(input, {
                        origError: origErr, triggerOp: op,
                        signal: window.__currentFFmpegSignal || null
                    });
                } catch (repairErr) {
                    // AbortError from a cancelled signal should propagate up so
                    // the caller (audio-enhancer cancel handler etc.) sees a
                    // clean abort instead of the original codec error.
                    if (repairErr && repairErr.name === 'AbortError') throw repairErr;
                    // tryRepair shouldn't otherwise throw, but be defensive
                    throw origErr;
                }
                if (!repaired) throw origErr;

                // The FFmpeg worker may be in a weird state after a container-
                // error exec. Reinit before retry.
                try { await fp.reinit(); } catch(_){}

                // Mark so the recovery wrapper doesn't try again on this file
                try { repaired.__audioRecovered = true; } catch(_){}
                if (!window.__audioRecoveredSet) {
                    try { window.__audioRecoveredSet = new WeakSet(); } catch(_){}
                }
                try { window.__audioRecoveredSet && window.__audioRecoveredSet.add(repaired); } catch(_){}

                try {
                    var result = await guarded.call(this, repaired, args, outName, inputName, preArgs, _retry);
                    if (result && typeof result === 'object') {
                        try { result.recoveredFromBroken = true; } catch(_){}
                    }
                    return result;
                } catch (retryErr) {
                    try {
                        if (window.__ae) window.__ae('tool_error', {
                            error_type: 'audio_recover_retry_failed',
                            op: op,
                            origErr: ((origErr && origErr.message) || '').substring(0, 200),
                            retryErr: ((retryErr && retryErr.message) || '').substring(0, 200),
                            ext: ((input.name || '').split('.').pop() || '').toLowerCase(),
                            sizeMB_in: Math.round((input.size || 0) / 1048576),
                            sizeMB_out: Math.round((repaired.size || 0) / 1048576)
                        });
                    } catch(_){}
                    throw retryErr;
                }
            }
        };
        recoveryWrapper.__autoGuarded = true;        // preserve flag for callers that check
        recoveryWrapper.__recoveryWrapped = true;
        recoveryWrapper.__guardedOriginal = initialGuarded;
        recoveryWrapper.__original = initialGuarded.__original;  // pass-through to raw process
        fp.process = recoveryWrapper;
        // Global install flag: subsequent wrappers (timbrica-eta, etc.) can
        // replace fp.process and lose the per-function flag, but the recovery
        // layer is still in the call chain. Tests / diagnostics use this.
        try { window.__audioRecoverInstalled = true; } catch(_){}
    }

    // ── FILE_LOST retry-on-foreground (Wave 7, 2026-06-02) ──────────────────
    // /pino telemetry 7 d: 49 wasm_process FILE_LOST + 14 raw NotReadableError
    // + 34 user_visible "File no longer accessible" = ~97 events / 7d on the
    // FFmpeg-WASM path. WebCodecs path got file-slice retry in Wave 2.B;
    // FFmpeg-WASM reads via FileReaderSync inside a Web Worker (WORKERFS),
    // which we can't intercept directly. Instead we wrap at the process()
    // boundary: catch FILE_LOST / NotReadableError, wait for foreground
    // visibility (up to 60 s, mirrors the encoder-reclaim wait pattern),
    // re-verify the file handle with a 4 KB pre-read, then re-init FFmpeg
    // and retry the operation ONCE. Dominant cause is Android backgrounding
    // the tab mid-process; coming back to foreground often lets the OS
    // re-grant the file handle. Pattern intentionally mirrors Wave 2.B's
    // _readSliceWithRetry so the two paths surface as a uniform recovery
    // shape in /pino (ffmpeg_file_lost_retry_attempt + _ok vs
    // hw_pipeline_slice_retry_attempt + _ok).
    if (fp.process && !fp.process.__fileLostRetried) {
        var innerProcess = fp.process;
        var fileLostWrapper = async function(input, args, outName, inputName, preArgs, _retry){
            try {
                return await innerProcess.call(this, input, args, outName, inputName, preArgs, _retry);
            } catch (err) {
                if (_retry) throw err;
                var msg = (err && err.message) || '';
                var nm = (err && err.name) || '';
                var isFileLost = /FILE_LOST/i.test(msg) || /NotReadableError/i.test(msg) ||
                                 /readAsArrayBuffer/.test(msg) ||
                                 nm === 'NotFoundError' || nm === 'NotReadableError';
                if (!isFileLost) throw err;
                if (!input || (!(input instanceof File) && !(input instanceof Blob))) throw err;
                try {
                    if (window.__ae) window.__ae('feature_use', {
                        slug: (window.__currentSlug || 'enhance-video'),
                        action: 'ffmpeg_file_lost_retry_attempt',
                        bg: document.visibilityState === 'hidden' ? 1 : 0,
                        fileMB: Math.round((input.size || 0) / 1048576)
                    });
                } catch (_) {}
                if (document.visibilityState !== 'visible') {
                    await new Promise(function(resolve) {
                        var done = false;
                        function settle() { if (done) return; done = true; clearTimeout(to); document.removeEventListener('visibilitychange', onVis); resolve(); }
                        function onVis() { if (document.visibilityState === 'visible') settle(); }
                        var to = setTimeout(settle, 60000);
                        document.addEventListener('visibilitychange', onVis);
                    });
                }
                try { await input.slice(0, 4096).arrayBuffer(); }
                catch (verifyErr) {
                    try {
                        if (window.__ae) window.__ae('feature_use', {
                            slug: (window.__currentSlug || 'enhance-video'),
                            action: 'ffmpeg_file_lost_retry_failed',
                            reason: 'verify_dead'
                        });
                    } catch (_) {}
                    throw err;
                }
                try { if (typeof fp.reinit === 'function') await fp.reinit(); } catch (_) {}
                try {
                    var ret = await innerProcess.call(this, input, args, outName, inputName, preArgs, 1);
                    try {
                        if (window.__ae) window.__ae('feature_use', {
                            slug: (window.__currentSlug || 'enhance-video'),
                            action: 'ffmpeg_file_lost_retry_ok'
                        });
                    } catch (_) {}
                    return ret;
                } catch (retryErr) {
                    try {
                        if (window.__ae) window.__ae('feature_use', {
                            slug: (window.__currentSlug || 'enhance-video'),
                            action: 'ffmpeg_file_lost_retry_failed',
                            reason: 'second_attempt'
                        });
                    } catch (_) {}
                    throw retryErr;
                }
            }
        };
        fileLostWrapper.__fileLostRetried = true;
        fileLostWrapper.__autoGuarded = true;
        fileLostWrapper.__recoveryWrapped = !!fp.process.__recoveryWrapped;
        fileLostWrapper.__guardedOriginal = fp.process.__guardedOriginal || fp.process;
        fileLostWrapper.__original = fp.process.__original;
        fp.process = fileLostWrapper;
    }

    // ── .processMulti ───────────────────────────────────────────────────────
    if (fp.processMulti && !fp.processMulti.__autoGuarded) {
        var origMulti = fp.processMulti;
        fp.processMulti = async function(inputs, args, outName){
            var explicitOp = consumeOperationTag(this);
            var operation = explicitOp || 'join';

            // Streaming: all inputs must be File/Blob (none pre-wrapped Uint8Array)
            var allStreamable = Array.isArray(inputs) && inputs.length > 0 && inputs.every(function(x){ return canStream(x, operation); });
            var totalBytes = 0;
            if (Array.isArray(inputs)) for (var _i = 0; _i < inputs.length; _i++) totalBytes += (inputs[_i] && inputs[_i].size) || 0;
            if (allStreamable && typeof fp.processMultiStreaming === 'function') {
                var streamT0 = Date.now();
                try {
                    var r = await fp.processMultiStreaming(inputs, args, outName);
                    emitPath('stream', operation, totalBytes, Date.now() - streamT0);
                    return r;
                } catch (err) {
                    if (!isMountSetupError(err)) throw err;
                    emitPath('stream_fallback_inmem', operation, totalBytes, Date.now() - streamT0);
                    console.warn('[ffmpeg] processMulti streaming mount failed, using in-memory path:', err.message);
                }
            } else if (totalBytes > 0) {
                emitPath('inmem_only', operation, totalBytes, 0);
            }

            // Classic + guard (sum of input sizes against device cap)
            if (Array.isArray(inputs) && inputs.length > 0) {
                var totalSize = 0;
                for (var i = 0; i < inputs.length; i++) totalSize += (inputs[i] && inputs[i].size) || 0;
                if (totalSize > 0) {
                    var guard = window.FFmpegMemoryGuard.check({
                        file: { size: totalSize, type: 'application/x-ffmpeg-multi' },
                        operation: operation
                    });
                    if (!guard.safe) {
                        throw buildGuardError({ size: totalSize, name: 'combined' }, guard, operation);
                    }
                }
            }
            return origMulti.call(this, inputs, args, outName);
        };
        fp.processMulti.__autoGuarded = true;
        fp.processMulti.__original = origMulti;
    }

    // ── .probe ──────────────────────────────────────────────────────────────
    if (fp.probe && !fp.probe.__autoStreamed) {
        var origProbe = fp.probe;
        fp.probe = async function(file){
            if (canStream(file) && typeof fp.probeStreaming === 'function') {
                try { return await fp.probeStreaming(file); }
                catch (err) {
                    if (!isMountSetupError(err)) throw err;
                    console.warn('[ffmpeg] probe streaming mount failed, using in-memory path:', err.message);
                }
            }
            return origProbe.call(this, file);
        };
        fp.probe.__autoStreamed = true;
        fp.probe.__original = origProbe;
    }

    // ── .processSegments ────────────────────────────────────────────────────
    if (fp.processSegments && !fp.processSegments.__autoStreamed) {
        var origSegments = fp.processSegments;
        fp.processSegments = async function(file, segments, args, outName){
            if (canStream(file, inferOperation(args)) && typeof fp.processSegmentsStreaming === 'function') {
                var streamT0 = Date.now();
                try {
                    var r = await fp.processSegmentsStreaming(file, segments, args, outName);
                    emitPath('stream', 'segments', file.size, Date.now() - streamT0);
                    return r;
                } catch (err) {
                    if (!isMountSetupError(err)) throw err;
                    emitPath('stream_fallback_inmem', 'segments', file.size, Date.now() - streamT0);
                    console.warn('[ffmpeg] processSegments streaming mount failed, using in-memory path:', err.message);
                }
            } else if (file && file.size) {
                emitPath('inmem_only', 'segments', file.size, 0);
            }
            return origSegments.call(this, file, segments, args, outName);
        };
        fp.processSegments.__autoStreamed = true;
        fp.processSegments.__original = origSegments;
    }

    // ── .concatSegments ─────────────────────────────────────────────────────
    if (fp.concatSegments && !fp.concatSegments.__autoStreamed) {
        var origConcat = fp.concatSegments;
        fp.concatSegments = async function(file, segments, outName, opts){
            if (canStream(file) && typeof fp.concatSegmentsStreaming === 'function') {
                var streamT0 = Date.now();
                try {
                    var r = await fp.concatSegmentsStreaming(file, segments, outName, opts);
                    emitPath('stream', 'concat', file.size, Date.now() - streamT0);
                    return r;
                } catch (err) {
                    if (!isMountSetupError(err)) throw err;
                    emitPath('stream_fallback_inmem', 'concat', file.size, Date.now() - streamT0);
                    console.warn('[ffmpeg] concatSegments streaming mount failed, using in-memory path:', err.message);
                }
            } else if (file && file.size) {
                emitPath('inmem_only', 'concat', file.size, 0);
            }
            return origConcat.call(this, file, segments, outName, opts);
        };
        fp.concatSegments.__autoStreamed = true;
        fp.concatSegments.__original = origConcat;
    }

    // ── .reencodeAndConcat ──────────────────────────────────────────────────
    if (fp.reencodeAndConcat && !fp.reencodeAndConcat.__autoStreamed) {
        var origReenc = fp.reencodeAndConcat;
        fp.reencodeAndConcat = async function(file, segments, args, outName, opts){
            // reencodeAndConcat is what enhance-video calls in the segment-based
            // OOM-recovery path. Use the operation tag from args so video-* gets
            // the lower threshold and we actually take the streaming path.
            if (canStream(file, inferOperation(args)) && typeof fp.reencodeAndConcatStreaming === 'function') {
                var streamT0 = Date.now();
                try {
                    var r = await fp.reencodeAndConcatStreaming(file, segments, args, outName, opts);
                    emitPath('stream', 'reencode_concat', file.size, Date.now() - streamT0);
                    return r;
                } catch (err) {
                    if (!isMountSetupError(err)) throw err;
                    emitPath('stream_fallback_inmem', 'reencode_concat', file.size, Date.now() - streamT0);
                    console.warn('[ffmpeg] reencodeAndConcat streaming mount failed, using in-memory path:', err.message);
                }
            } else if (file && file.size) {
                emitPath('inmem_only', 'reencode_concat', file.size, 0);
            }
            return origReenc.call(this, file, segments, args, outName, opts);
        };
        fp.reencodeAndConcat.__autoStreamed = true;
        fp.reencodeAndConcat.__original = origReenc;
    }

    // ── output contract: pin the pixel format ───────────────────────────────
    // libx264 / libx265 with no `-pix_fmt` inherit the SOURCE's, so a 10-bit
    // input (every HDR iPhone clip since iOS 13) comes out as H.264 High 10 or
    // HEVC Main 10 — profiles Windows refuses with 0xC00D11B1 while Chromium
    // decodes them in software. The tool sees a working preview, telemetry sees
    // nothing, and the user's desktop player sees a file that will not open.
    //
    // 37 hand-rolled call sites across 16 blades hit this, several building
    // their args dynamically (a `.map()` swapping `copy`→`libx264`, a
    // `.splice()` rewriting the encoder in place), so "remember the flag" is
    // not a policy that can hold. Pin it once, here, on the way to FFmpeg.
    // CodecMuxRules owns the rule; this file only owns the interception.
    //
    // Installed LAST so it is the outermost wrapper: the streaming / memory
    // decorators receive args that already satisfy the contract.
    {
        // ⚠️ Список ДОЛЖЕН покрывать каждую точку входа, через которую в FFmpeg
        // едут аргументы кодера. До 2026-08-16 здесь стояли только первые пять —
        // а сам процессор к тому времени отрастил ещё девять. Мимо контракта
        // ходил в том числе `processMultiStreaming`, которым /video-merge делает
        // склейку с переходом: жалоба #1412 («Долго, итоговый файл не открылся»)
        // — это выдача High 4:4:4 Predictive (`avc1.f4001f` в телеметрии), потому
        // что x264 без `-pix_fmt` наследует формат исходника. Воспроизведено:
        // 10-битный и 4:4:4 исходник давали на выходе yuv444p, который телефоны
        // и телевизоры не декодируют.
        // ⚠️ `format=yuv420p` в фильтрах НЕ считается заявкой (см. комментарий в
        // codec-mux-rules.js): он стоит в хвосте графа, а слушает кодер только
        // `-pix_fmt`. Проверка от протухания — FfmpegOutputContractCoverageTest:
        // он выводит список точек входа ИЗ КОДА и падает, если появилась новая.
        var PINNED = [
            ['process', 1], ['processStreaming', 1], ['processMulti', 1],
            ['processSegments', 2], ['reencodeAndConcat', 2],
            ['processMultiStreaming', 1], ['processSegmentsStreaming', 2],
            ['reencodeAndConcatStreaming', 2], ['concatPiecesStreaming', 2],
            ['processWithRotationFix', 1], ['applyRotationCorrection', 1],
            ['processWithSignal', 2], ['reverseChunked', 2],
            ['processReverse', 1], ['smartTwoPassTrimStreaming', 4],
        ];
        var _pinReported = {};
        PINNED.forEach(function(entry){
            var name = entry[0], argIdx = entry[1];
            var orig = fp[name];
            if (typeof orig !== 'function' || orig.__pixFmtGuarded) return;
            fp[name] = function(){
                var a = Array.prototype.slice.call(arguments);
                var before = a[argIdx];
                var after = CodecMuxRules.enforceVideoOutputContract(before);
                if (after !== before) {
                    a[argIdx] = after;
                    // Fires only for callers that never stated a pixel format —
                    // once per entry point per page, because a retry ladder can
                    // call process() a dozen times for one export and we want a
                    // count of TOOLS that still hand-roll encoder args, not of
                    // attempts. A busy counter names the next tool to migrate
                    // onto CodecMuxRules.videoEncodeArgs().
                    if (!_pinReported[name]) {
                        _pinReported[name] = true;
                        try {
                            if (window.__ae) window.__ae('feature_use', {
                                slug: (window.__TOOL_SLUG || location.pathname.split('/').pop() || ''),
                                action: 'pix_fmt_pinned', via: name,
                            });
                        } catch (_) {}
                    }
                }
                return orig.apply(this, a);
            };
            fp[name].__pixFmtGuarded = true;
            fp[name].__original = orig;
        });
        /* A later decorator (webcodecs-encoder, the OOM-recovery wrapper) may
           re-wrap these and shadow the per-function flag — the pin still runs,
           it just stops being visible on `fp.process`. Publish the fact once,
           on window, so tests and diagnostics can see it without depending on
           who wrapped whom last. */
        window.__pixFmtGuardInstalled = true;
    }
}

wrapWhenReady();

})();
